import { css, CSSResultGroup, LitElement, PropertyValues } from "lit";
import { property } from "lit/decorators.js";
import type { EltType, Attr } from "../pandoc";
import { OutDesignNode } from "../ast";
import { Selector, SelectorEventDetail } from "../rules";
import { getSharedStyles, areOriginalStylesEnabled, isStructureViewEnabled, areNotesCollapsed } from "./shared";

export abstract class OutDesignNodeElement extends LitElement {
    @property() node: OutDesignNode;
    @property({ type: Boolean, reflect: true }) selected: boolean = false;
    @property({ type: Boolean, reflect: true }) tempSelected: boolean = false;
    @property({ type: Boolean, reflect: true }) originalStylesEnabled: boolean = areOriginalStylesEnabled();
    @property({ type: Boolean, reflect: true }) structureViewEnabled: boolean = isStructureViewEnabled();

    static styles = css`
    :host([selected]), :host([tempSelected]) {
        outline: var(--wa-focus-ring-style) 2px var(--wa-color-focus);
        background-color: rgba(62, 150, 255, 0.1);
    }
    :host([tempSelected]) {
        outline-color: rgba(62, 150, 255, 0.5);
        background-color: rgba(62, 150, 255, 0.05);
    }
    ` as CSSResultGroup;

    protected createRenderRoot() {
        const root = super.createRenderRoot() as ShadowRoot;

        this.addSharedStyles(root)

        this.originalStylesEnabled = true;
        return root;
    }

    protected updated(_changedProperties: PropertyValues): void {
        this.originalStylesEnabled = areOriginalStylesEnabled()
        this.structureViewEnabled = isStructureViewEnabled()
    }

    private addSharedStyles(root: ShadowRoot): void {
        const sheet = getSharedStyles();

        if (sheet) {
            const sheets = root.adoptedStyleSheets;

            if (!sheets.includes(sheet)) {
                root.adoptedStyleSheets = [...sheets, sheet];
            }
        }
    }

    private removeSharedStyles(root: ShadowRoot): void {
        const sheet = getSharedStyles();

        if (sheet) {
            const sheets = root.adoptedStyleSheets;

            if (sheets.includes(sheet)) {
                const index = sheets.indexOf(sheet, 0);
                if (index > -1) {
                    sheets.splice(index, 1);
                }
                // root.adoptedStyleSheets = [...sheets, sheet];
            }
        }
    }

    get attr(): Attr | undefined {
        return this.node?.attr;
    }

    get type(): EltType {
        return this.node.type;
    }
    
    get id(): string {
        return this.node?.attr?.[0] ?? "";
    }
    
    get classes(): Array<String> {
        return this.node?.attr?.[1] ?? [];
    }
    
    get kvs(): Array<[string, any]> {
        return this.node?.attr?.[2] ?? [];
    }

    get data(): Record<string, string | number> | undefined {
        return this.node.data;
    }

    get originalInDesignStyle(): string | undefined {
        if(!this.data ||
            !this.data.originalAttr ||
            !(Array.isArray(this.data.originalAttr)) ||
            !(Array.isArray(this.data.originalAttr[2]))
        ) return undefined
        const originalAttr = this.data.originalAttr[2][0]
        if(!originalAttr) return
        const index = originalAttr.indexOf("role")
        if(index >= 0) return originalAttr[index + 1]
    }

    connectedCallback() {
        super.connectedCallback()
        document.addEventListener("selector-added", this.handleSelectorAdded);
        document.addEventListener("temp-selector-added", this.handleTempSelectorAdded);
        document.addEventListener("toggle-styles-view", this.toggleStylesView);
        document.addEventListener("toggle-structure-view", this.toggleStructureView);
    }

    disconnectedCallback(): void {
        document.removeEventListener("selector-added", this.handleSelectorAdded);
        document.removeEventListener("temp-selector-added", this.handleTempSelectorAdded);
        document.removeEventListener("toggle-styles-view", this.toggleStylesView);
        document.removeEventListener("toggle-structure-view", this.toggleStructureView);
        super.disconnectedCallback();
    }

    private handleSelectorAdded = (e: Event) => {
        const ev = e as CustomEvent<SelectorEventDetail>;
        this.toggleSelectionIfMatchSelectors(ev.detail.part, ev.detail.mode, false);
    }

    private handleTempSelectorAdded = (e: Event) => {
        const ev = e as CustomEvent<SelectorEventDetail>;
        this.toggleSelectionIfMatchSelectors(ev.detail.part, ev.detail.mode, true);
    }

    private toggleStylesView = () => {
        if(this.originalStylesEnabled != areOriginalStylesEnabled()) {
            if(this.originalStylesEnabled) {
                this.removeSharedStyles(this.renderRoot as ShadowRoot)
            } else this.addSharedStyles(this.renderRoot as ShadowRoot)
            this.originalStylesEnabled = !this.originalStylesEnabled
        }
    }

    private toggleStructureView = () => {
        if(this.structureViewEnabled != isStructureViewEnabled()) {
            this.structureViewEnabled = !this.structureViewEnabled
        }
    }

    toggleSelectionIfMatchSelectors(selectors: Selector[], mode: "append" | "replace", temp: boolean) {
        let found: boolean = false;
        selectors.forEach( (sel) => {
            if(this.toggleSelectionIfMatchSelector(sel)) {
                temp ? this.tempSelected = true : this.selected = true;
                found = true;
                return;
            }
        });
        if (!found && mode == "replace") temp ? this.tempSelected = false : this.selected = false;
    }

    private toggleSelectionIfMatchSelector(selector: Selector): boolean {
        const emptyCond = Object.keys(selector).length === 0;
        const typeCondition : boolean = !(selector.type) || selector.type === this.type;
        const idCondition : boolean = !(selector.id) || selector.id === this.id;
        const classesConditions : boolean = !(selector.classes) || selector.classes.every(r=> this.classes.includes(r))

        if(!emptyCond && typeCondition && idCondition && classesConditions) return true;
        return false;
    }
}